import { UserContextInterface, UsersContext } from "contexts/UsersContext";
import { IComment } from "interfaces/comment";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { UserType } from "interfaces/user";
import { CommentFeedAccordion } from "components/Comments/CommentFeedAccordion";
import { useWriteCaseActionComment } from "hooks/comments/case-action/useWriteCaseActionComment";
import { useReadCaseActionComments } from "hooks/comments/case-action/useReadCaseActionComments";

interface CaseActionCommentSectionProps {
  actionAssigneeId?: number | null;
  actionId: number;
}

export const CaseActionCommentSection = ({
  actionAssigneeId,
  actionId
}: CaseActionCommentSectionProps): JSX.Element => {
  const {
    loading,
    error,
    users,
    nonServiceAccountUsers,
    internalUsers,
    getFullName
  }: UserContextInterface = useContext(UsersContext);

  // Exclude users of type 'External' unless they are the action assignee.
  const filteredUsers = useMemo(() => {
    return users.filter(
      user =>
        user?.user_type !== UserType.EXTERNAL || user.id === actionAssigneeId
    );
  }, [users, actionAssigneeId]);

  const [comments, setComments] = useState<IComment[]>([]);

  const {
    loading: commentsListLoading,
    getCaseActionComments
  } = useReadCaseActionComments(actionId);

  async function onCommentsChanged(): Promise<void> {
    setComments(await getCaseActionComments());
  }

  useEffect(() => {
    onCommentsChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    createCaseActionComment,
    updateCaseActionComment,
    deleteCaseActionComment
  } = useWriteCaseActionComment(actionId, onCommentsChanged);

  return (
    <UsersContext.Provider
      value={{
        loading,
        error,
        users: filteredUsers,
        nonServiceAccountUsers,
        internalUsers,
        getFullName
      }}
    >
      <CommentFeedAccordion
        loading={commentsListLoading}
        comments={comments}
        addComment={createCaseActionComment}
        updateComment={updateCaseActionComment}
        deleteComment={deleteCaseActionComment}
      />
    </UsersContext.Provider>
  );
};
